import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Shared Request-Access form used by Campus / Panels / Sprint product pages.
 * Posts to POST /api/product-enquiry with { product, name, email, company, country, role, message }.
 * Props:
 *   - product: "campus" | "panels" | "sprint"
 *   - productLabel: display label e.g. "Operon Campus"
 *   - accent: tailwind colour prefix for accent CTAs e.g. "amber" | "emerald" | "violet"
 */
export default function ProductEnquiryForm({ product, productLabel, accent = "cyan" }) {
  const [form, setForm] = useState({
    name: "", email: "", company: "", country: "", role: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const isValid =
    form.name.trim() &&
    /^\S+@\S+\.\S+$/.test(form.email) &&
    form.company.trim() &&
    form.country.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/product-enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product, ...form }),
      });
      if (!res.ok) throw new Error("failed");
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong — please try again or email marco@operon-hq.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const bg = { cyan: "bg-cyan-500 hover:bg-cyan-400", amber: "bg-amber-500 hover:bg-amber-400", emerald: "bg-emerald-500 hover:bg-emerald-400", violet: "bg-violet-500 hover:bg-violet-400", teal: "bg-teal-500 hover:bg-teal-400" }[accent];
  const txt = "text-[#060B1C]";

  if (submitted) {
    return (
      <div className="rounded-2xl border border-[#243768] bg-[#0F1D40]/60 p-8 text-center" data-testid={`${product}-success`}>
        <div className={`w-14 h-14 mx-auto mb-4 rounded-full bg-${accent}-500/15 border border-${accent}-500/40 flex items-center justify-center`}>
          <CheckCircle2 className={`w-7 h-7 text-${accent}-400`} />
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">Thanks — enquiry received.</h3>
        <p className="text-[#9DB0D6]">
          We&apos;ll be in touch at <span className="text-white">{form.email}</span> within a couple of working days.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-2xl border border-[#243768] bg-[#0F1D40]/50 p-6 md:p-8 space-y-5" data-testid={`${product}-form`}>
      <div>
        <div className={`text-[10px] font-mono text-${accent}-300 tracking-[0.3em] uppercase mb-2`}>
          Private beta · Request access
        </div>
        <h3 className="text-2xl font-bold text-white">Tell us about your context</h3>
        <p className="text-[#9DB0D6] text-sm mt-1">
          {productLabel} is in private beta. A few quick details and we&apos;ll get back to you personally.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-[#E9EEF9]">Your name <span className={`text-${accent}-400`}>*</span></Label>
          <Input value={form.name} onChange={set("name")} placeholder="Alex Smith"
            className="bg-[#0B1735] border-[#243768] text-white placeholder:text-[#6C7EA6]" data-testid={`${product}-input-name`} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#E9EEF9]">Role (optional)</Label>
          <Input value={form.role} onChange={set("role")} placeholder="Park director"
            className="bg-[#0B1735] border-[#243768] text-white placeholder:text-[#6C7EA6]" data-testid={`${product}-input-role`} />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-[#E9EEF9]">Email <span className={`text-${accent}-400`}>*</span></Label>
        <Input type="email" value={form.email} onChange={set("email")} placeholder="alex@yourorg.com"
          className="bg-[#0B1735] border-[#243768] text-white placeholder:text-[#6C7EA6]" data-testid={`${product}-input-email`} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-[#E9EEF9]">Company / Organisation <span className={`text-${accent}-400`}>*</span></Label>
          <Input value={form.company} onChange={set("company")} placeholder="Acme Science Park"
            className="bg-[#0B1735] border-[#243768] text-white placeholder:text-[#6C7EA6]" data-testid={`${product}-input-company`} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#E9EEF9]">Country <span className={`text-${accent}-400`}>*</span></Label>
          <Input value={form.country} onChange={set("country")} placeholder="United Kingdom"
            className="bg-[#0B1735] border-[#243768] text-white placeholder:text-[#6C7EA6]" data-testid={`${product}-input-country`} />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-[#E9EEF9]">Short message</Label>
        <Textarea value={form.message} onChange={set("message")} rows={4}
          placeholder={`Tell us a little about your ${product === "panels" ? "events and audience" : product === "campus" ? "park and tenants" : "team and the diagnostic you're hoping to run"}…`}
          className="bg-[#0B1735] border-[#243768] text-white placeholder:text-[#6C7EA6] resize-none"
          data-testid={`${product}-input-message`} />
      </div>

      <Button type="submit" disabled={!isValid || submitting}
        className={`${bg} ${txt} font-semibold rounded-full px-6 py-6 h-auto text-base disabled:opacity-50 w-full sm:w-auto`}
        data-testid={`${product}-submit`}>
        {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>) : (<>Send request<ArrowRight className="w-4 h-4 ml-2" /></>)}
      </Button>
    </form>
  );
}
